import { MRT_ColumnDef } from 'material-react-table';
import CustomMaterialReactTable from 'components/features/MaterialReactTable/CustomMaterialReactTable';
import { formatDateTimeFull, parseDateToLocal } from 'utils/date';
import {
    usePutApiRequestsByRequestIdAcceptMutation,
    usePutApiRequestsByRequestIdRejectMutation,
} from 'store/userOrg/enhancedUserOrgApi';
import { useGetApiAdministrationRequestsQuery } from 'store/userOrg/enhancedUserOrgAdministrationApi';
import { ButtonGroup } from '@mui/material';
import { Button } from '@mui/material';
import { useMemo } from 'react';
import useGAHandleClick from 'hooks/useGAHandleClick';
import { throwSuccessToast } from 'utils';
import { successMessages } from 'constants/messages';

const columns: MRT_ColumnDef<any>[] = [
    {
        accessorKey: 'Email',
        header: 'Email',
        size: 150,
        enableClickToCopy: true,
    },
    {
        accessorKey: 'LastModification',
        header: 'Last Modification Date',
        size: 150,
        Cell: ({
            cell: {
                row: {
                    original: { LastModification },
                },
            },
        }) => formatDateTimeFull(LastModification),
        enableClickToCopy: true,
    },
    {
        accessorKey: 'ProjectShortName',
        header: 'Project Short Name',
        size: 200,
        enableClickToCopy: true,
    },
    {
        accessorKey: 'state',
        header: 'State',
        size: 200,
        enableClickToCopy: true,
    },
    {
        accessorKey: 'UserName',
        header: 'User Name',
        size: 200,
        enableClickToCopy: true,
    },
];

const ProjectRequestsManagement = () => {
    const googleAnalytics = useGAHandleClick();
    const { data: requests, isLoading: requestsIsLoading } = useGetApiAdministrationRequestsQuery({
        type: 'SelfOnboarding',
    });

    const requestsData = useMemo(
        () =>
            Array.isArray(requests)
                ? requests
                      .map(request => ({
                          ...request,
                          state: request.State === 4 ? 'Accepted' : request.State === 5 ? 'Rejected' : 'Pending',
                      }))
                      .sort((a, b) => {
                          const timeB = parseDateToLocal(b.LastModification)?.valueOf() || 0;
                          const timeA = parseDateToLocal(a.LastModification)?.valueOf() || 0;
                          return timeB - timeA;
                      })
                : [],
        [requests]
    );

    const [accept, { isLoading: acceptIsLoading }] = usePutApiRequestsByRequestIdAcceptMutation();
    const [reject, { isLoading: rejectIsLoading }] = usePutApiRequestsByRequestIdRejectMutation();

    const onAccept = async (requestId: string | null | undefined) => {
        if (requestId) {
            await accept({ requestId: requestId }).unwrap();

            throwSuccessToast({ message: successMessages.accepted });

            googleAnalytics({
                label: 'AcceptProjectRequest',
                action: 'Submit',
                category: 'Submission',
            });
        }
    };

    const onReject = async (requestId: string | null | undefined) => {
        if (requestId) {
            await reject({ requestId: requestId }).unwrap();

            throwSuccessToast({ message: successMessages.rejected });

            googleAnalytics({
                label: 'RejectProjectRequest',
                action: 'Submit',
                category: 'Submission',
            });
        }
    };

    return (
        <CustomMaterialReactTable
            isLoading={requestsIsLoading}
            columns={columns}
            data={requestsData}
            enableGrouping={true}
            rowActions={({ row }) =>
                row.original.State !== 4 &&
                row.original.State !== 5 && (
                    <ButtonGroup variant='text'>
                        <Button
                            variant='contained'
                            disabled={acceptIsLoading || rejectIsLoading}
                            onClick={() => {
                                onAccept(row.original.RequestId);
                            }}>
                            Accept
                        </Button>
                        <Button
                            variant='contained'
                            color='error'
                            disabled={acceptIsLoading || rejectIsLoading}
                            onClick={() => {
                                onReject(row.original.RequestId);
                            }}>
                            Reject
                        </Button>
                    </ButtonGroup>
                )
            }
        />
    );
};

export default ProjectRequestsManagement;
